/**
 * Global client-error capture. Sends unhandled errors + axios failures to
 * `POST /api/platform/client-errors` so super admins can see what's breaking
 * in client browsers without waiting for support tickets.
 *
 * Wired up once from `index.js` at app startup. Best-effort — never throws,
 * never blocks user flow.
 */
import axios from 'axios';

const ENDPOINT = `${process.env.REACT_APP_BACKEND_URL}/api/platform/client-errors`;
const DEDUPE_WINDOW_MS = 30_000; // don't report the same error twice within 30s
const recentErrors = new Map();

// Noise-filter config: transient infra errors (Cloudflare 520-524, network blips,
// timeouts) are reported ONLY if they recur this many times in the rolling window
// for the same URL. A single hiccup during a polling tick is not actionable.
const TRANSIENT_THRESHOLD = 3;
const TRANSIENT_WINDOW_MS = 5 * 60_000; // 5 minutes
const TRANSIENT_CLOUDFLARE_STATUSES = new Set([520, 521, 522, 523, 524]);
const transientHits = new Map(); // key -> [timestamps]

function _dedupeKey(payload) {
  return `${payload.message}|${(payload.file || '')}|${payload.line || ''}`;
}

function _shouldReport(payload) {
  const key = _dedupeKey(payload);
  const now = Date.now();
  // Sweep stale entries
  for (const [k, ts] of recentErrors.entries()) {
    if (now - ts > DEDUPE_WINDOW_MS) recentErrors.delete(k);
  }
  if (recentErrors.has(key)) return false;
  recentErrors.set(key, now);
  return true;
}

function _isTransientAxios(status) {
  // status 0 / undefined => CORS preflight failure, DNS, network drop, request aborted
  if (status === 0 || status === undefined || status === null) return true;
  // Cloudflare edge errors are transient by definition (origin reachability)
  if (TRANSIENT_CLOUDFLARE_STATUSES.has(status)) return true;
  // Gateway timeouts are usually transient cold-start / proxy hiccups
  if (status === 502 || status === 503 || status === 504) return true;
  return false;
}

function _shouldReportTransient(url, status) {
  const now = Date.now();
  const key = `${status || 'NET'}|${url || ''}`;
  const hits = transientHits.get(key) || [];
  // Drop expired entries
  const fresh = hits.filter((t) => now - t < TRANSIENT_WINDOW_MS);
  fresh.push(now);
  transientHits.set(key, fresh);
  // Periodic cleanup so the map doesn't grow unbounded
  if (transientHits.size > 200) {
    for (const [k, arr] of transientHits.entries()) {
      const kept = arr.filter((t) => now - t < TRANSIENT_WINDOW_MS);
      if (kept.length === 0) transientHits.delete(k);
      else transientHits.set(k, kept);
    }
  }
  return fresh.length >= TRANSIENT_THRESHOLD;
}

// Patterns that originate outside our control — browser extensions, in-app
// webview teardown (Instagram/Facebook on Android), CORS-masked third-party
// script errors. Reporting these wastes Super Admin attention and never points
// at an actionable code fix on our side.
const THIRD_PARTY_NOISE_PATTERNS = [
  // CORS-masked: any script loaded cross-origin without `crossorigin` attr.
  // Always opaque — there is nothing to debug.
  /^Script error\.?$/i,
  // Android WebView teardown bug — fires when Instagram/Facebook in-app
  // browsers destroy the Java<->JS bridge while JS is still running.
  // Not our code; browser-side bug, no fix possible.
  /Java object is gone/i,
  /Error invoking postMessage/i,
  // Browser extensions (varies; the most common signals)
  /chrome-extension:\/\//i,
  /moz-extension:\/\//i,
  /safari-(web-)?extension:\/\//i,
  // Network failures already handled by the axios interceptor — these are
  // the equivalent fired by window.onerror when fetch() fails.
  /^Network Error$/i,
];

function _isThirdPartyNoise(message, file) {
  const target = `${message || ''}\n${file || ''}`;
  return THIRD_PARTY_NOISE_PATTERNS.some((re) => re.test(target));
}

function _report(payload) {
  // Filter at the source — anything we recognise as third-party noise
  // never even enters the dedup window.
  if (_isThirdPartyNoise(payload.message, payload.file)) return;
  if (!_shouldReport(payload)) return;
  try {
    const token = (typeof localStorage !== 'undefined') ? localStorage.getItem('token') : null;
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;
    // Fire-and-forget. Don't wait.
    fetch(ENDPOINT, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        message: payload.message || 'Unknown error',
        stack: payload.stack || null,
        source: payload.source || 'manual',
        url: typeof window !== 'undefined' ? window.location.href : null,
        user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
        file: payload.file || null,
        line: payload.line || null,
        column: payload.column || null,
        extra: payload.extra || {},
      }),
      keepalive: true,
    }).catch(() => { /* swallow — already in error state */ });
  } catch {
    // Reporter must never throw
  }
}

let _installed = false;

export function installGlobalErrorReporter() {
  if (_installed || typeof window === 'undefined') return;
  _installed = true;

  // Uncaught JS errors
  window.addEventListener('error', (event) => {
    _report({
      message: event.message,
      stack: event.error && event.error.stack,
      file: event.filename,
      line: event.lineno,
      column: event.colno,
      source: 'window.onerror',
    });
  });

  // Unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    _report({
      message: (reason && (reason.message || String(reason))) || 'Unhandled promise rejection',
      stack: reason && reason.stack,
      source: 'unhandledrejection',
    });
  });

  // Axios response errors (5xx, network failures). We only report the rare ones
  // — 4xx are user-facing and already handled by individual pages.
  axios.interceptors.response.use(
    (r) => r,
    (error) => {
      try {
        const status = error.response && error.response.status;
        const url = error.config && error.config.url;
        // Skip the report endpoint itself to avoid a loop
        if (url && url.includes('/platform/client-errors')) return Promise.reject(error);
        // Skip 4xx — these are expected business errors handled by pages
        if (status && status < 500 && status !== 0) return Promise.reject(error);
        // Skip if the user is offline — not actionable
        if (typeof navigator !== 'undefined' && navigator.onLine === false) {
          return Promise.reject(error);
        }
        // Skip request cancellations / aborts — these aren't real failures
        if (axios.isCancel && axios.isCancel(error)) return Promise.reject(error);
        if (error.code === 'ERR_CANCELED' || error.message === 'canceled') {
          return Promise.reject(error);
        }
        // Suppress transient infra errors (Cloudflare 520-524, network blips,
        // gateway timeouts) until they recur N times in the window — a single
        // polling hiccup is noise, repeated failures are signal.
        if (_isTransientAxios(status) && !_shouldReportTransient(url, status)) {
          return Promise.reject(error);
        }
        _report({
          message: `API ${status || 'NETWORK'} ${error.config?.method?.toUpperCase() || ''} ${url || ''}`,
          stack: error.stack,
          source: 'axios',
          extra: {
            status,
            method: error.config?.method,
            response_data: error.response?.data ? JSON.stringify(error.response.data).slice(0, 500) : null,
          },
        });
      } catch { /* swallow */ }
      return Promise.reject(error);
    }
  );
}

// Manual reporter — components can call this directly from catch blocks
export function reportClientError(message, extra = {}) {
  _report({ message: String(message), source: 'manual', extra });
}

// Exported for unit tests only — do not use in app code.
export const __test__ = {
  _isTransientAxios,
  _shouldReportTransient,
  reset: () => { recentErrors.clear(); transientHits.clear(); },
  TRANSIENT_THRESHOLD,
  TRANSIENT_WINDOW_MS,
};
